import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { KYC_STATUS_LABEL, KYC_STATUS_COLOR } from '@/lib/constants';

const EMPTY = { name: '', firm_name: '', mobile: '', email: '', tax_id: '', address: '', license_number: '' };

export default function AdminAgents() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => api.get('/agents').then((r) => setItems(r.data)).catch(() => toast.error('Failed to load agents')), []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setForm(EMPTY); setCreating(true); };
  const startEdit = (a) => {
    setEdit(a);
    setForm({
      name: a.name || '', firm_name: a.firm_name || '', mobile: a.mobile || '',
      email: a.email || '', tax_id: a.tax_id || '', address: a.address || '',
      license_number: a.license_number || ''
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      if (edit) {
        await api.put(`/agents/${edit.id}`, form);
        toast.success('Agent updated');
      } else {
        await api.post('/agents', form);
        toast.success('Agent added');
      }
      setEdit(null); setCreating(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/agents/${confirmDelete.id}`);
      toast.success('Agent deleted');
      setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete (in use)'); }
  };

  const isOpen = creating || !!edit;
  const closeForm = (v) => { if (!v) { setCreating(false); setEdit(null); setForm(EMPTY); } };

  return (
    <div>
      <PageHeader title="Agents" subtitle="Real-estate / brokerage agents who get assigned to properties for listings and visits" actions={
        <Button onClick={startCreate} data-testid="admin-agents-add"><Plus className="h-4 w-4 mr-1" /> Add agent</Button>
      } />

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Firm</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">License</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Assigned</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">KYC</th>
              <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No agents yet. Click "Add agent" to create the first one.</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t hover:bg-muted/40" data-testid={`agent-row-${a.id}`}>
                <td className="px-3 py-2 font-medium">{a.name}</td>
                <td className="px-3 py-2">{a.firm_name || '—'}</td>
                <td className="px-3 py-2">{a.mobile || '—'}</td>
                <td className="px-3 py-2">{a.email || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{a.license_number || '—'}</td>
                <td className="px-3 py-2 text-xs">{(a.assigned_property_ids || []).length} properties</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={KYC_STATUS_COLOR[a.kyc_status] || ''}>{KYC_STATUS_LABEL[a.kyc_status] || a.kyc_status}</Badge>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => startEdit(a)} data-testid={`agent-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(a)} data-testid={`agent-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={isOpen} onOpenChange={closeForm}>
        <DialogContent className="sm:max-w-[560px] max-h-[92dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'Edit agent' : 'Add agent'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name <span className="text-destructive">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-form-name" /></div>
            <div className="sm:col-span-2"><Label>Firm / brokerage name</Label><Input value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} /></div>
            <div><Label>RERA / License #</Label><Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => closeForm(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={save} className="w-full sm:w-auto" data-testid="agent-form-save">{edit ? 'Save changes' : 'Add agent'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.name}</strong>. Agents assigned to any property cannot be deleted — reassign first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="agent-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
